import { createContext, useContext, useEffect, useState, ReactNode, useCallback } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { useNavigate } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import { identifyUser, resetAnalyticsUser } from '@/lib/analytics';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  subscribed: boolean;
  inTrial: boolean;
  hasHadTrial: boolean;
  subscriptionEnd: string | null;
  checkSubscription: () => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  session: null,
  loading: true,
  subscribed: false,
  inTrial: false,
  hasHadTrial: false,
  subscriptionEnd: null,
  checkSubscription: async () => {},
  signOut: async () => {},
});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

// Network calls to Supabase (session refresh, edge functions) have no built-in
// timeout. If one stalls (flaky connection, cold-start, transient outage),
// awaiting it forever leaves `loading`/subscription checks stuck and the UI
// spins indefinitely (e.g. SubscriptionGate never resolves). Race against a
// timeout so callers always get a settled promise.
const withTimeout = <T,>(promise: Promise<T>, ms: number, label: string): Promise<T> =>
  Promise.race([
    promise,
    new Promise<T>((_, reject) =>
      setTimeout(() => reject(new Error(`${label} timed out after ${ms}ms`)), ms)
    ),
  ]);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [subscribed, setSubscribed] = useState(false);
  const [inTrial, setInTrial] = useState(false);
  const [hasHadTrial, setHasHadTrial] = useState(false);
  const [subscriptionEnd, setSubscriptionEnd] = useState<string | null>(null);
  const navigate = useNavigate();

  const syncRealtimeAuth = useCallback((accessToken?: string | null) => {
    if (!accessToken) return;
    supabase.realtime.setAuth(accessToken);
  }, []);

  const checkSubscription = useCallback(async (currentSession?: Session | null) => {
    console.log('[AUTH] checkSubscription() start', {
      isNative: Capacitor.isNativePlatform(),
      platform: Capacitor.getPlatform(),
      hasSessionParam: Boolean(currentSession),
    });

    let sessionToUse = currentSession ?? null;

    // Prefer the session from auth callbacks, fallback to current persisted session
    if (!sessionToUse) {
      const { data, error: sessionError } = await withTimeout(
        supabase.auth.getSession(),
        8000,
        'getSession'
      ).catch((err) => ({ data: { session: null }, error: err }) as any);
      const latestSession = data?.session ?? null;

      if (sessionError || !latestSession) {
        console.log('[AUTH] No valid session for subscription check', { sessionError });
        setSubscribed(false);
        setInTrial(false);
        setSubscriptionEnd(null);
        return;
      }

      sessionToUse = latestSession;
    }

    // If session is close to expiry, refresh and use the refreshed token
    const expiresAt = sessionToUse.expires_at;
    if (expiresAt && expiresAt * 1000 - Date.now() < 60000) {
      console.log('[AUTH] Session close to expiry, refreshing...');
      try {
        const {
          data: { session: refreshedSession },
          error: refreshError,
        } = await withTimeout(supabase.auth.refreshSession(), 8000, 'refreshSession');
        if (refreshError || !refreshedSession) {
          console.error('[AUTH] Failed to refresh session:', refreshError);
          setSubscribed(false);
          setInTrial(false);
          setSubscriptionEnd(null);
          return;
        }
        sessionToUse = refreshedSession;
      } catch (timeoutErr) {
        // Refresh stalled (e.g. flaky network) — proceed with the existing
        // token rather than hanging the subscription check forever.
        console.warn('[AUTH] refreshSession timed out, using existing session:', timeoutErr);
      }
    }

    try {
      const accessToken = sessionToUse.access_token;

      console.log('[AUTH] Invoking subscription checks', {
        hasAccessToken: Boolean(accessToken),
      });

      // Check both Stripe and RevenueCat subscriptions (force explicit JWT header)
      const [stripeResult, revenuecatResult] = await Promise.all([
        withTimeout(
          supabase.functions.invoke('check-subscription', {
            headers: { Authorization: `Bearer ${accessToken}` },
          }),
          8000,
          'check-subscription'
        ).catch((err) => {
          console.error('[AUTH] Stripe check error:', err);
          return { data: { subscribed: false, inTrial: false }, error: err } as any;
        }),
        withTimeout(
          supabase.functions.invoke('verify-receipt', {
            headers: { Authorization: `Bearer ${accessToken}` },
          }),
          8000,
          'verify-receipt'
        ).catch((err) => {
          console.error('[AUTH] RevenueCat check error:', err);
          return { data: { subscribed: false, inTrial: false }, error: err } as any;
        }),
      ]);

      const stripeData = stripeResult.data || {};
      const revenuecatData = revenuecatResult.data || {};

      // User is subscribed if either payment system shows active subscription
      const isSubscribed = stripeData.subscribed || revenuecatData.subscribed;
      const isInTrial = stripeData.inTrial || revenuecatData.inTrial;
      const hadTrial = stripeData.hasHadTrial || revenuecatData.hasHadTrial || isInTrial || isSubscribed;

      // Use the latest subscription end date
      let endDate = null;
      if (stripeData.subscription_end && revenuecatData.subscription_end) {
        endDate =
          new Date(stripeData.subscription_end) > new Date(revenuecatData.subscription_end)
            ? stripeData.subscription_end
            : revenuecatData.subscription_end;
      } else {
        endDate = stripeData.subscription_end || revenuecatData.subscription_end || null;
      }

      console.log('[AUTH] Subscription check result:', {
        stripe: stripeData,
        revenuecat: revenuecatData,
        final: { subscribed: isSubscribed, inTrial: isInTrial, subscriptionEnd: endDate },
      });

      setSubscribed(isSubscribed);
      setInTrial(isInTrial);
      setHasHadTrial(hadTrial);
      setSubscriptionEnd(endDate);
    } catch (error) {
      console.error('[AUTH] Error in checkSubscription:', error);
      setSubscribed(false);
      setInTrial(false);
      setSubscriptionEnd(null);
    }
  }, []);

  useEffect(() => {
    let isMounted = true;

    // Set up auth state listener (must be synchronous)
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      if (!isMounted) return;

      if (session?.access_token) {
        syncRealtimeAuth(session.access_token);
      }
      
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);

      if (session?.user) {
        identifyUser(session.user.id, { email: session.user.email ?? undefined });
        // Small delay to ensure session is fully established
        setTimeout(() => {
          if (isMounted) {
            void checkSubscription(session);
          }
        }, 500);
      } else {
        resetAnalyticsUser();
        setSubscribed(false);
        setInTrial(false);
        setHasHadTrial(false);
        setSubscriptionEnd(null);
      }
    });

    // Hard safety net: never leave the app stuck on a full-screen spinner if
    // the initial session read stalls (flaky network / storage).
    const loadingWatchdog = setTimeout(() => {
      if (isMounted) setLoading(false);
    }, 10000);

    // Check for existing session and refresh if needed
    withTimeout(supabase.auth.getSession(), 8000, 'getSession')
      .catch((err) => {
        console.warn('[Auth] getSession timed out:', err);
        return { data: { session: null }, error: err } as any;
      })
      .then(async ({ data: { session } }: any) => {
      if (!isMounted) return;
      
      
      let currentSession = session;
      
      // If session exists but is expired or close to expiry, try to refresh
      if (currentSession?.expires_at) {
        const expiresAt = currentSession.expires_at * 1000;
        const now = Date.now();
        const timeUntilExpiry = expiresAt - now;
        
        // Refresh if expired or will expire in next 5 minutes
        if (timeUntilExpiry < 5 * 60 * 1000) {
          console.log('[Auth] Session expired or expiring soon, refreshing...');
          try {
            const { data: { session: refreshedSession }, error: refreshError } =
              await withTimeout(supabase.auth.refreshSession(), 8000, 'refreshSession');

            if (!refreshError && refreshedSession) {
              currentSession = refreshedSession;
              console.log('[Auth] Session refreshed successfully');
            } else {
              console.warn('[Auth] Failed to refresh session:', refreshError);
              // If refresh fails, clear the session
              currentSession = null;
            }
          } catch (timeoutErr) {
            // Refresh stalled (e.g. flaky network). Don't leave `loading`
            // stuck forever (that was leaving /chats spinning indefinitely
            // after Onboarding redirected there) — fall back to the
            // not-yet-refreshed session so the app can still render.
            console.warn('[Auth] refreshSession timed out, using existing session:', timeoutErr);
          }
        }
      }
      
      setSession(currentSession);
      setUser(currentSession?.user ?? null);
      setLoading(false);

      if (currentSession?.access_token) {
        syncRealtimeAuth(currentSession.access_token);
      }

      if (currentSession?.user) {
        setTimeout(() => {
          if (isMounted) {
            void checkSubscription(currentSession);
          }
        }, 500);
      }
    });

    return () => {
      isMounted = false;
      clearTimeout(loadingWatchdog);
      subscription.unsubscribe();
    };
  }, [checkSubscription, syncRealtimeAuth]);

  // Periodic subscription check every 5 minutes (reduces rate limiting)
  useEffect(() => {
    if (!session) return;

    const interval = setInterval(() => {
      // Only check if we have a valid session
      supabase.auth.getSession().then(({ data: { session: currentSession } }) => {
        if (currentSession) {
          void checkSubscription(currentSession);
        }
      });
    }, 5 * 60 * 1000); // 5 minutes

    return () => clearInterval(interval);
  }, [session, checkSubscription]);

  // Re-check subscription when the app/tab regains focus so that the
  // "Start Free Trial" button disappears promptly after a user returns
  // from the Stripe checkout page.
  useEffect(() => {
    if (!session) return;

    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        supabase.auth.getSession().then(({ data: { session: currentSession } }) => {
          if (currentSession) {
            void checkSubscription(currentSession);
          }
        });
      }
    };

    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, [session, checkSubscription]);

  const signOut = async () => {
    try {
      // Local scope guarantees local session cleanup even if server session is already invalid
      await supabase.auth.signOut({ scope: 'local' });
    } catch (error) {
      console.warn('[Auth] Local signOut failed, clearing local state anyway:', error);
    }
    // Clear per-user localStorage flags so they don't bleed into a subsequent login
    localStorage.removeItem('gcc_dismissed_paywall');
    setUser(null);
    setSession(null);
    setSubscribed(false);
    setInTrial(false);
    setHasHadTrial(false);
    setSubscriptionEnd(null);
    // Send users back to the language picker so they always start there.
    navigate('/');
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        session,
        loading,
        subscribed,
        inTrial,
        hasHadTrial,
        subscriptionEnd,
        checkSubscription,
        signOut,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
